import React, { useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CameraAlt, CheckCircle } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { uploadPhoto, walkInCheckIn } from './services';
import { printLabels } from './printLabels';
import { CheckInRecord } from './types';

interface WalkInFormState {
  childFirstName: string;
  childLastName: string;
  dateOfBirth: Date | null;
  parentName: string;
  parentPhone: string;
  allergies: string;
  specialNotes: string;
  photoUrl: string | null;
}

const EMPTY_FORM: WalkInFormState = {
  childFirstName: '',
  childLastName: '',
  dateOfBirth: null,
  parentName: '',
  parentPhone: '',
  allergies: '',
  specialNotes: '',
  photoUrl: null,
};

type WizardStep = 'enter_child' | 'add_more' | 'check_in' | 'print_tags';

interface Props {
  open: boolean;
  onClose: () => void;
}

const GuestCheckInWizard: React.FC<Props> = ({ open, onClose }) => {
  const [step, setStep] = useState<WizardStep>('enter_child');
  const [pendingForms, setPendingForms] = useState<WalkInFormState[]>([]);
  const [currentForm, setCurrentForm] = useState<WalkInFormState>(EMPTY_FORM);
  const [checkedInRecords, setCheckedInRecords] = useState<CheckInRecord[]>([]);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReset = () => {
    setStep('enter_child');
    setPendingForms([]);
    setCurrentForm(EMPTY_FORM);
    setCheckedInRecords([]);
    setCheckingIn(false);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCurrentForm((f) => ({
        ...f,
        photoUrl: ev.target?.result as string,
      }));
    };
    reader.readAsDataURL(file);
    // reset so the same file can be selected again
    e.target.value = '';
  };

  const handleAddChild = () => {
    if (
      !currentForm.childFirstName.trim() ||
      !currentForm.childLastName.trim() ||
      !currentForm.dateOfBirth ||
      !currentForm.parentName.trim() ||
      !currentForm.parentPhone.trim()
    ) {
      setError(
        'First name, last name, date of birth, parent name, and parent phone are required.'
      );
      return;
    }
    setError('');
    setPendingForms((prev) => [...prev, currentForm]);
    setCurrentForm(EMPTY_FORM);
    setStep('add_more');
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    setError('');
    const sharedPin = Math.floor(1000 + Math.random() * 9000).toString();
    try {
      const records: CheckInRecord[] = [];
      for (const form of pendingForms) {
        let resolvedPhoto: string | null = null;
        if (form.photoUrl) {
          resolvedPhoto = await uploadPhoto(form.photoUrl);
        }
        const result = await walkInCheckIn({
          childFirstName: form.childFirstName.trim(),
          childLastName: form.childLastName.trim(),
          dateOfBirth: form.dateOfBirth,
          photoUrl: resolvedPhoto,
          parentName: form.parentName.trim() || undefined,
          parentPhone: form.parentPhone.trim() || undefined,
          allergies: form.allergies.trim() || undefined,
          specialNotes: form.specialNotes.trim() || undefined,
          adminId: 'guest-kiosk',
          pickupPin: sharedPin,
        });
        records.push({
          id: result.id,
          parentId: result.parentId,
          familyId: result.familyId,
          childId: result.childId,
          childFirstName: form.childFirstName.trim(),
          childLastName: form.childLastName.trim(),
          dateOfBirth: form.dateOfBirth,
          photoUrl: resolvedPhoto,
          parentName: form.parentName.trim() || undefined,
          parentPhone: form.parentPhone.trim() || undefined,
          allergies: form.allergies.trim() || undefined,
          specialNotes: form.specialNotes.trim() || undefined,
          pickupPin: result.pickupPin,
          checkInTime: new Date(),
          checkOutTime: null,
          createdOn: new Date(),
          isWalkIn: true,
        });
      }
      setCheckedInRecords(records);
      setStep('print_tags');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  const handlePrint = async () => {
    await printLabels(checkedInRecords, 'guest-kiosk');
    handleClose();
  };

  const stepTitle: Record<WizardStep, string> = {
    enter_child: 'Child Information',
    add_more: 'Add Another Child?',
    check_in: 'Ready to Check In',
    print_tags: 'Check-In Complete!',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{ sx: { mt: 4 } }}
    >
      <DialogTitle>{stepTitle[step]}</DialogTitle>

      <DialogContent>
        {/* ── Step: Enter child info ── */}
        {step === 'enter_child' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {pendingForms.length > 0 && (
              <Alert severity="success" sx={{ mb: 1 }}>
                {pendingForms.length} child
                {pendingForms.length > 1 ? 'ren' : ''} added. Fill in the next
                child below.
              </Alert>
            )}

            {/* Photo capture */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                style={{ display: 'none' }}
                onChange={handlePhotoCapture}
              />
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{ position: 'relative', cursor: 'pointer' }}
              >
                <Avatar
                  src={currentForm.photoUrl ?? undefined}
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    fontSize: 42,
                  }}
                >
                  {!currentForm.photoUrl &&
                    (currentForm.childFirstName?.[0] ?? (
                      <CameraAlt sx={{ fontSize: 38 }} />
                    ))}
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    bgcolor: 'primary.dark',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2,
                  }}
                >
                  <CameraAlt sx={{ fontSize: 15, color: 'white' }} />
                </Box>
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: -1 }}
            >
              Tap to take photo
            </Typography>

            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name *"
                value={currentForm.childFirstName}
                onChange={(e) =>
                  setCurrentForm((f) => ({
                    ...f,
                    childFirstName: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                autoFocus
              />
              <TextField
                label="Last Name *"
                value={currentForm.childLastName}
                onChange={(e) =>
                  setCurrentForm((f) => ({
                    ...f,
                    childLastName: e.target.value,
                  }))
                }
                fullWidth
                size="small"
              />
            </Stack>
            <DatePicker<Date>
              label="Date of Birth *"
              value={currentForm.dateOfBirth}
              onChange={(val: Date | null) =>
                setCurrentForm((f) => ({ ...f, dateOfBirth: val }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
            <TextField
              label="Parent Name *"
              value={currentForm.parentName}
              onChange={(e) =>
                setCurrentForm((f) => ({ ...f, parentName: e.target.value }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label="Parent Phone *"
              value={currentForm.parentPhone}
              onChange={(e) =>
                setCurrentForm((f) => ({ ...f, parentPhone: e.target.value }))
              }
              fullWidth
              size="small"
              type="tel"
              inputProps={{ maxLength: 15 }}
            />
            <TextField
              label="Allergies"
              value={currentForm.allergies}
              onChange={(e) =>
                setCurrentForm((f) => ({ ...f, allergies: e.target.value }))
              }
              fullWidth
              size="small"
              placeholder="e.g. peanuts, dairy"
            />
            <TextField
              label="Special Notes"
              value={currentForm.specialNotes}
              onChange={(e) =>
                setCurrentForm((f) => ({ ...f, specialNotes: e.target.value }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Stack>
        )}

        {/* ── Step: Add more? ── */}
        {step === 'add_more' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body1" fontWeight={500}>
              Children added so far:
            </Typography>
            {pendingForms.map((f, i) => (
              <Box key={i}>
                {i > 0 && <Divider sx={{ mb: 2 }} />}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    src={f.photoUrl ?? undefined}
                    sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}
                  >
                    {!f.photoUrl && f.childFirstName[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {f.childFirstName} {f.childLastName}
                    </Typography>
                    {f.allergies && (
                      <Typography variant="body2" color="warning.dark">
                        ⚠ {f.allergies}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
            <Divider />
            <Typography variant="body1" textAlign="center" sx={{ pt: 1 }}>
              Would you like to add another child?
            </Typography>
          </Stack>
        )}

        {/* ── Step: Review & Check In ── */}
        {step === 'check_in' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The following children will be checked in:
            </Typography>
            {pendingForms.map((f, i) => (
              <Box key={i}>
                {i > 0 && <Divider sx={{ mb: 1 }} />}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    src={f.photoUrl ?? undefined}
                    sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}
                  >
                    {!f.photoUrl && f.childFirstName[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {f.childFirstName} {f.childLastName}
                    </Typography>
                    {f.parentName && (
                      <Typography variant="body2" color="text.secondary">
                        Parent: {f.parentName}
                      </Typography>
                    )}
                    {f.allergies && (
                      <Typography variant="body2" color="warning.dark">
                        ⚠ {f.allergies}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Stack>
        )}

        {/* ── Step: Print name tags ── */}
        {step === 'print_tags' && (
          <Stack spacing={2} sx={{ pt: 1 }} alignItems="center">
            <CheckCircle color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h6" textAlign="center">
              {checkedInRecords.length} child
              {checkedInRecords.length > 1 ? 'ren' : ''} checked in!
            </Typography>
            {checkedInRecords.map((r, i) => (
              <Box key={i} textAlign="center">
                <Typography variant="body1" fontWeight={600}>
                  {r.childFirstName} {r.childLastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pickup PIN: <strong>{r.pickupPin}</strong>
                </Typography>
              </Box>
            ))}
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ pt: 1 }}
            >
              Print name tags and pick them up at the front desk. Attach them to
              your child before entering.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'enter_child' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleAddChild}>
              Add Child
            </Button>
          </>
        )}

        {step === 'add_more' && (
          <>
            <Button variant="outlined" onClick={() => setStep('enter_child')}>
              Add Another Child
            </Button>
            <Button variant="contained" onClick={() => setStep('check_in')}>
              Done, Continue Check In
            </Button>
          </>
        )}

        {step === 'check_in' && (
          <>
            <Button onClick={() => setStep('add_more')} disabled={checkingIn}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleCheckIn}
              disabled={checkingIn}
              startIcon={
                checkingIn ? <CircularProgress size={16} /> : undefined
              }
            >
              {checkingIn ? 'Checking In…' : 'Check In'}
            </Button>
          </>
        )}

        {step === 'print_tags' && (
          <>
            <Button variant="contained" onClick={handlePrint}>
              Print Name Tags
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GuestCheckInWizard;
